import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUpcomingGroupSessions, useJoinGroupSession } from '@/hooks';
import { motion } from 'framer-motion';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import {
  Users,
  Calendar,
  Clock,
  BookOpen,
  UserPlus,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { SEO, createBreadcrumbSchema } from '@/components/seo';

const TYPES = [
  { key: '', en: 'All', ar: 'الكل' },
  { key: 'Quran Recitation', en: 'Recitation', ar: 'تلاوة' },
  { key: 'Hifz Circle', en: 'Hifz Circle', ar: 'حلقة حفظ' },
  { key: 'Tajweed Workshop', en: 'Tajweed', ar: 'تجويد' },
  { key: 'Tafseer Study', en: 'Tafseer', ar: 'تفسير' },
];

const TYPE_COLORS: Record<string, string> = {
  'Quran Recitation': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  'Hifz Circle': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  'Tajweed Workshop': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  'Tafseer Study': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
};

const GroupSessions = () => {
  const { isRTL } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [typeFilter, setTypeFilter] = useState('');
  const { data: sessions, isLoading } = useUpcomingGroupSessions(typeFilter || undefined);
  const joinSession = useJoinGroupSession();

  const items = (sessions as any)?.sessions || (sessions as any[]) || [];

  const handleJoin = async (sessionName: string) => {
    if (!isAuthenticated) {
      toast.error(isRTL ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      return;
    }
    try {
      await joinSession.mutateAsync({ session_name: sessionName });
      toast.success(isRTL ? 'تم الانضمام بنجاح' : 'Joined successfully');
    } catch {
      toast.error(isRTL ? 'فشل الانضمام' : 'Failed to join');
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema([
        { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
        { name: isRTL ? 'الجلسات الجماعية' : 'Group Sessions', url: 'https://hudanlilmuttaqeen.com/group-sessions' }
      ])
    ]
  };

  return (
    <Layout>
      <SEO
        title={isRTL ? 'الجلسات الجماعية | هُدًى للمتقين' : 'Group Sessions | Hudan Lil Muttaqeen'}
        description={isRTL
          ? 'انضم إلى حلقات جماعية لتلاوة القرآن والتجويد وحفظ القرآن مع معلمين معتمدين وطلاب من حول العالم.'
          : 'Join live group Halaqah sessions for Quran recitation, Tajweed, and Hifz with certified teachers and learners from around the world.'}
        structuredData={structuredData}
      />
      <PageHeader
        title="Group Sessions"
        titleAr="الحلقات الجماعية"
        subtitle="Join group Halaqah sessions for collaborative learning"
        subtitleAr="انضم إلى حلقات جماعية للتعلم التعاوني"
        size="sm"
      />

      <div className="container py-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Type Filter */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-fit">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                typeFilter === t.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {isRTL ? t.ar : t.en}
            </button>
          ))}
        </div>

        {/* Session Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((session: any, i: number) => {
              const typeColor = TYPE_COLORS[session.halaqah_type] || TYPE_COLORS['Quran Recitation'];
              const capacity = session.max_participants || 8;
              const current = session.current_participants || 0;
              const isFull = current >= capacity;
              const capacityPct = Math.round((current / capacity) * 100);

              return (
                <motion.div
                  key={session.name}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="border-border hover:border-primary/20 transition-all hover:shadow-md">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex items-start justify-between mb-3">
                        <Badge className={`text-xs ${typeColor}`}>
                          {session.halaqah_type}
                        </Badge>
                        {session.price > 0 ? (
                          <span className="text-sm font-bold text-primary">${session.price}</span>
                        ) : (
                          <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
                            {isRTL ? 'مجاني' : 'Free'}
                          </Badge>
                        )}
                      </div>

                      <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
                        {session.title}
                      </h3>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4 shrink-0" />
                          <span>{session.scheduled_at || session.day_of_week}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4 shrink-0" />
                          <span>{session.duration_minutes || 60} {isRTL ? 'دقيقة' : 'min'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <BookOpen className="h-4 w-4 shrink-0" />
                          <span>{session.teacher_name}</span>
                        </div>
                      </div>

                      {/* Capacity */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {current}/{capacity} {isRTL ? 'مشاركين' : 'participants'}
                          </span>
                          <span>{capacityPct}%</span>
                        </div>
                        <Progress
                          value={capacityPct}
                          className={`h-1.5 ${isFull ? '[&>div]:bg-red-500' : ''}`}
                        />
                      </div>

                      <Button
                        onClick={() => handleJoin(session.name)}
                        disabled={isFull || joinSession.isPending}
                        className="w-full gap-2"
                        variant={isFull ? 'outline' : 'default'}
                      >
                        {joinSession.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isFull ? null : (
                          <UserPlus className="h-4 w-4" />
                        )}
                        {isFull
                          ? (isRTL ? 'مكتمل' : 'Full')
                          : (isRTL ? 'انضم' : 'Join')}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title={isRTL ? 'لا توجد حلقات حالياً' : 'No group sessions available'}
            description={isRTL ? 'تحقق مرة أخرى لاحقاً للحلقات الجديدة' : 'Check back later for new sessions'}
          />
        )}
      </div>
    </Layout>
  );
};

export default GroupSessions;
